import React, { useState } from "react";
import PropTypes from "prop-types";

const PREDEFINED_REASONS = [
  "Poor video quality or incomplete property tour",
  "Incomplete or missing property information",
  "Violates listing policy or guidelines",
  "Inappropriate or offensive content",
  "Duplicate or fraudulent listing",
  "Unrealistic or suspicious pricing",
  "Custom reason",
];

const PropertyRejectionModal = ({ isOpen, onConfirm, onCancel, isLoading }) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const handleConfirm = () => {
    const finalReason =
      selectedReason === "Custom reason" ? customReason : selectedReason;

    if (!finalReason.trim()) {
      alert("Please provide a rejection reason.");
      return;
    }

    onConfirm(finalReason);
    resetForm();
  };

  const resetForm = () => {
    setSelectedReason("");
    setCustomReason("");
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Reject Property</h2>
        
        <p className="text-sm text-gray-600 mb-6">
          Please select or provide a reason for rejecting this property. This will be sent to the property owner via email.
        </p>

        <div className="mb-4">
          <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
            Rejection Reason
          </label>
          <select
            id="rejectionReason"
            value={selectedReason}
            onChange={(e) => {
              setSelectedReason(e.target.value);
              if (e.target.value !== "Custom reason") {
                setCustomReason("");
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
            disabled={isLoading}
          >
            <option value="">Select a reason...</option>
            {PREDEFINED_REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </select>
        </div>

        {selectedReason === "Custom reason" && (
          <div className="mb-6">
            <label htmlFor="customReason" className="block text-sm font-medium text-gray-700 mb-2">
              Custom Reason
            </label>
            <textarea
              id="customReason"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="Enter your custom rejection reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none disabled:opacity-50"
              rows="4"
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500 mt-2">
              {customReason.length}/500 characters
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 text-sm text-gray-700 font-medium rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading || !selectedReason}
            className="flex-1 min-w-0 px-3 py-2 bg-red-600 text-sm text-white font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {isLoading ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

PropertyRejectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

PropertyRejectionModal.defaultProps = {
  isLoading: false,
};

export default PropertyRejectionModal;
